import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { EventTransportOverview, ID } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

export function TransportScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [overview, setOverview] = React.useState<EventTransportOverview[] | null>(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [overviewError, setOverviewError] = React.useState<string | null>(null);
  const [addingRouteFor, setAddingRouteFor] = React.useState<EventTransportOverview | null>(null);
  const [addingRequestFor, setAddingRequestFor] = React.useState<EventTransportOverview | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      setOverview(await service.transport.getOverview());
    } catch (err) {
      setOverviewError(errorMessage(err));
    } finally {
      setOverviewLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading transport" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const deleteRoute = async (routeId: ID) => {
    setActionError(null);
    try {
      await service.transport.deleteRoute(routeId);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const deleteRequest = async (requestId: ID) => {
    setActionError(null);
    try {
      await service.transport.deleteRequest(requestId);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const assignToRoute = async (requestId: ID, routeId: string) => {
    setActionError(null);
    try {
      await service.transport.updateRequest(requestId, { routeId: routeId || null });
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Logistics" title="Transport" meta={[{ icon: "car", label: "Whole wedding overview" }]} />
      <div className="pob-pad">
        {overviewLoading && !overview && <LoadingState label="Loading transport" />}
        {overviewError && <ErrorState message={overviewError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}

        {overview && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            {overview.map((row) => (
              <Card
                key={row.eventId}
                eyebrow={row.eventName}
                title={`${row.requiredSeats} seats needed · ${row.availableSeats} available`}
                action={<span style={{ display: "flex", gap: "var(--space-2)" }}>
                  <Button size="sm" onClick={() => setAddingRouteFor(row)}>Add route</Button>
                  <Button size="sm" onClick={() => setAddingRequestFor(row)}>Add request</Button>
                </span>}
              >
                <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-4)", flexWrap: "wrap" }}>
                  {row.shortfall > 0 && <Badge tone="error">Shortfall: {row.shortfall} seat{row.shortfall === 1 ? "" : "s"} short</Badge>}
                  {row.notYetRequested.length > 0 && <Badge tone="accent">{row.notYetRequested.length} flagged, not yet requested</Badge>}
                </div>

                <DataTable
                  rows={row.routes}
                  emptyLabel="No routes yet"
                  columns={[
                    { key: "name", header: "Route", render: (route) => (
                      <div>
                        <span style={{ font: "var(--type-label)", display: "block" }}>{route.name}</span>
                        <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{route.fromLabel} → {route.toLabel}{route.time ? ` · ${route.time}` : ""}</span>
                      </div>
                    ) },
                    { key: "capacity", header: "Capacity", width: 100, render: (route) => `${route.requests.reduce((sum, r) => sum + r.guestCount, 0)}/${route.capacity}` },
                    { key: "driver", header: "Driver", muted: true, width: 120 },
                    { key: "actions", header: "", width: 90, render: (route) => <Button size="sm" variant="danger" onClick={() => deleteRoute(route.id)}>Remove</Button> }
                  ]}
                />

                {(row.unassignedRequests.length > 0 || row.routes.some((r) => r.requests.length > 0)) && (
                  <div style={{ marginTop: "var(--space-4)" }}>
                    <DataTable
                      rows={[...row.routes.flatMap((r) => r.requests), ...row.unassignedRequests]}
                      columns={[
                        { key: "householdName", header: "Household" },
                        { key: "guestCount", header: "Guests", width: 80 },
                        { key: "pickupPoint", header: "Pickup point", muted: true },
                        { key: "route", header: "Route", width: 180, render: (req) => (
                          <Select size="sm" value={req.routeId || ""} placeholder="Unassigned" onChange={(event) => assignToRoute(req.id, event.target.value)} options={row.routes.map((r) => ({ value: r.id, label: r.name }))} />
                        ) },
                        { key: "actions", header: "", width: 90, render: (req) => <Button size="sm" variant="danger" onClick={() => deleteRequest(req.id)}>Remove</Button> }
                      ]}
                    />
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {addingRouteFor && (
        <RouteDialog eventId={addingRouteFor.eventId} onClose={() => setAddingRouteFor(null)} onSaved={async () => { setAddingRouteFor(null); await load(); }} />
      )}
      {addingRequestFor && (
        <RequestDialog eventId={addingRequestFor.eventId} households={workspace.households} onClose={() => setAddingRequestFor(null)} onSaved={async () => { setAddingRequestFor(null); await load(); }} />
      )}
    </div>
  );
}

function RouteDialog({ eventId, onClose, onSaved }: { eventId: ID; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [name, setName] = React.useState("");
  const [fromLabel, setFromLabel] = React.useState("");
  const [toLabel, setToLabel] = React.useState("");
  const [time, setTime] = React.useState("");
  const [capacity, setCapacity] = React.useState("10");
  const [driver, setDriver] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsed = Number(capacity);
    if (!name.trim() || !fromLabel.trim() || !toLabel.trim()) { setFormError("Fill in the route name and from/to points."); return; }
    if (!Number.isFinite(parsed) || parsed < 1) { setFormError("Capacity must be at least 1."); return; }
    try {
      await service.transport.createRoute({ eventId, name: name.trim(), fromLabel: fromLabel.trim(), toLabel: toLabel.trim(), time: time || undefined, capacity: parsed, driver: driver || undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Add transport route"
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add route"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Route name"><Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Shuttle 1" /></Field>
        <Field label="Pickup point"><Input value={fromLabel} onChange={(event) => setFromLabel(event.target.value)} placeholder="Serena Hotel" /></Field>
        <Field label="Destination"><Input value={toLabel} onChange={(event) => setToLabel(event.target.value)} placeholder="Venue" /></Field>
        <Field label="Time (optional)"><Input value={time} onChange={(event) => setTime(event.target.value)} placeholder="17:30" /></Field>
        <Field label="Capacity"><Input value={capacity} type="number" onChange={(event) => setCapacity(event.target.value)} /></Field>
        <Field label="Driver (optional)"><Input value={driver} onChange={(event) => setDriver(event.target.value)} /></Field>
      </div>
    </Dialog>
  );
}

function RequestDialog({ eventId, households, onClose, onSaved }: { eventId: ID; households: { id: ID; name: string }[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [householdId, setHouseholdId] = React.useState(households[0]?.id || "");
  const [guestCount, setGuestCount] = React.useState("1");
  const [pickupPoint, setPickupPoint] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsed = Number(guestCount);
    if (!householdId) { setFormError("Add a household first."); return; }
    if (!Number.isFinite(parsed) || parsed < 1) { setFormError("Guest count must be at least 1."); return; }
    try {
      await service.transport.createRequest({ householdId, eventId, guestCount: parsed, pickupPoint: pickupPoint || undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Add transport request"
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add request"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Household"><Select value={householdId} onChange={(event) => setHouseholdId(event.target.value)} options={households.map((h) => ({ value: h.id, label: h.name }))} /></Field>
        <Field label="Guests travelling"><Input value={guestCount} type="number" onChange={(event) => setGuestCount(event.target.value)} /></Field>
        <Field label="Pickup point (optional)"><Input value={pickupPoint} onChange={(event) => setPickupPoint(event.target.value)} placeholder="Hotel lobby" /></Field>
      </div>
    </Dialog>
  );
}
